(() => {
  const buildEmbedSrc = (type, id, opts) => {
    const muted = opts.muted !== false;
    const autoplay = opts.autoplay !== false;
    const loop = opts.loop !== false;

    if (type === 'vimeo') {
      const params = [`autoplay=${autoplay ? 1 : 0}`, `muted=${muted ? 1 : 0}`, 'playsinline=1', 'controls=0'];
      if (loop) params.push('loop=1');
      return `https://player.vimeo.com/video/${id}?${params.join('&')}`;
    }

    const params = ['enablejsapi=1', `autoplay=${autoplay ? 1 : 0}`, `mute=${muted ? 1 : 0}`, 'playsinline=1', 'controls=0'];
    if (loop) params.push(`loop=1&playlist=${id}`);
    return `https://www.youtube.com/embed/${id}?${params.join('&')}`;
  };

  const buildMedia = (card, opts) => {
    opts = opts || {};
    const muted = opts.muted !== false;
    const autoplay = opts.autoplay !== false;
    const loop = opts.loop !== false;

    if (card.dataset.videoSource === 'embed') {
      const type = card.dataset.videoEmbedType;
      const id = card.dataset.videoEmbedId;
      if (!id) return null;

      const iframe = document.createElement('iframe');
      iframe.src = buildEmbedSrc(type, id, { muted, autoplay, loop });
      iframe.setAttribute('allow', 'autoplay; fullscreen; picture-in-picture');
      iframe.allowFullscreen = true;
      iframe.setAttribute('frameborder', '0');
      iframe.dataset.kvsEmbedType = type;
      iframe.dataset.kvsEmbedId = id;
      return iframe;
    }

    const template = card.querySelector('template[data-kvs-template]');
    if (!template) return null;

    const fragment = template.content.cloneNode(true);
    const video = fragment.querySelector('video');
    if (video) {
      video.muted = muted;
      video.loop = loop;
      video.playsInline = true;
      video.setAttribute('playsinline', '');
    }
    return video;
  };

  const initKvs = (root) => {
    if (!root || root.dataset.kvsInit) return;
    root.dataset.kvsInit = 'true';

    const cards = Array.from(root.querySelectorAll('[data-kvs-card]'));
    if (!cards.length) return;

    const fsRoot = root.querySelector('[data-kvs-fullscreen]');
    const fsMedia = fsRoot ? fsRoot.querySelector('[data-kvs-fullscreen-media]') : null;
    const fsPlayPause = fsRoot ? fsRoot.querySelector('[data-kvs-playpause]') : null;
    const fsMuteBtn = fsRoot ? fsRoot.querySelector('[data-kvs-mute]') : null;
    const fsPrev = fsRoot ? fsRoot.querySelector('[data-kvs-prev]') : null;
    const fsNext = fsRoot ? fsRoot.querySelector('[data-kvs-next]') : null;

    let fsIndex = -1;
    let fsMutedState = true;

    const updateFsControlsState = () => {
      if (fsPlayPause) {
        const playIcon = fsPlayPause.querySelector('.kvs__icon-play');
        const pauseIcon = fsPlayPause.querySelector('.kvs__icon-pause');
        if (playIcon) playIcon.hidden = true;
        if (pauseIcon) pauseIcon.hidden = false;
      }
      if (fsMuteBtn) {
        const mutedIcon = fsMuteBtn.querySelector('.kvs__icon-muted');
        const unmutedIcon = fsMuteBtn.querySelector('.kvs__icon-unmuted');
        if (mutedIcon) mutedIcon.hidden = !fsMutedState;
        if (unmutedIcon) unmutedIcon.hidden = fsMutedState;
      }
      if (fsPrev) fsPrev.disabled = fsIndex <= 0;
      if (fsNext) fsNext.disabled = fsIndex >= cards.length - 1;
    };

    const renderFullscreenAt = (index) => {
      if (!fsMedia) return;
      const card = cards[index];
      if (!card) return;

      fsMedia.innerHTML = '';
      const mediaEl = buildMedia(card, { muted: fsMutedState, autoplay: true, loop: true });
      if (!mediaEl) return;

      fsMedia.appendChild(mediaEl);
      fsIndex = index;
      updateFsControlsState();
    };

    const openFullscreen = (index) => {
      if (!fsRoot) return;
      fsMutedState = true;
      renderFullscreenAt(index);
      fsRoot.classList.add('kvs__fullscreen--open');
      fsRoot.setAttribute('aria-hidden', 'false');
      document.body.classList.add('kvs-fullscreen-lock');
    };

    const closeFullscreen = () => {
      if (!fsRoot) return;
      fsRoot.classList.remove('kvs__fullscreen--open');
      fsRoot.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('kvs-fullscreen-lock');
      if (fsMedia) fsMedia.innerHTML = '';
      fsIndex = -1;
    };

    if (fsRoot) {
      fsRoot.querySelectorAll('[data-kvs-close]').forEach((el) => {
        el.addEventListener('click', closeFullscreen);
      });
    }

    if (fsPrev) {
      fsPrev.addEventListener('click', () => {
        if (fsIndex > 0) renderFullscreenAt(fsIndex - 1);
      });
    }

    if (fsNext) {
      fsNext.addEventListener('click', () => {
        if (fsIndex < cards.length - 1) renderFullscreenAt(fsIndex + 1);
      });
    }

    if (fsPlayPause) {
      fsPlayPause.addEventListener('click', () => {
        const mediaEl = fsMedia ? fsMedia.firstElementChild : null;
        if (!mediaEl) return;

        const playIcon = fsPlayPause.querySelector('.kvs__icon-play');
        const pauseIcon = fsPlayPause.querySelector('.kvs__icon-pause');
        const currentlyPlaying = pauseIcon && !pauseIcon.hidden;

        if (mediaEl.tagName === 'VIDEO') {
          if (mediaEl.paused) {
            mediaEl.play().catch(() => {});
            playIcon.hidden = true;
            pauseIcon.hidden = false;
          } else {
            mediaEl.pause();
            playIcon.hidden = false;
            pauseIcon.hidden = true;
          }
        } else {
          const type = mediaEl.dataset.kvsEmbedType;
          const id = mediaEl.dataset.kvsEmbedId;
          mediaEl.src = buildEmbedSrc(type, id, {
            muted: fsMutedState,
            autoplay: !currentlyPlaying,
            loop: true,
          });
          playIcon.hidden = !currentlyPlaying ? true : false;
          pauseIcon.hidden = currentlyPlaying ? true : false;
        }
      });
    }

    if (fsMuteBtn) {
      fsMuteBtn.addEventListener('click', () => {
        fsMutedState = !fsMutedState;
        const mediaEl = fsMedia ? fsMedia.firstElementChild : null;

        if (mediaEl) {
          if (mediaEl.tagName === 'VIDEO') {
            mediaEl.muted = fsMutedState;
          } else {
            const type = mediaEl.dataset.kvsEmbedType;
            const id = mediaEl.dataset.kvsEmbedId;
            mediaEl.src = buildEmbedSrc(type, id, { muted: fsMutedState, autoplay: true, loop: true });
          }
        }

        updateFsControlsState();
      });
    }

    document.addEventListener('keydown', (event) => {
      if (!fsRoot || !fsRoot.classList.contains('kvs__fullscreen--open')) return;
      if (event.key === 'Escape') closeFullscreen();
      if (event.key === 'ArrowUp' && fsIndex > 0) renderFullscreenAt(fsIndex - 1);
      if (event.key === 'ArrowDown' && fsIndex < cards.length - 1) renderFullscreenAt(fsIndex + 1);
    });

    cards.forEach((card, idx) => {
      const mediaWrap = card.querySelector('[data-kvs-media]');
      const toggle = card.querySelector('[data-kvs-toggle]');
      if (!mediaWrap || !toggle) return;

      const playIcon = toggle.querySelector('.kvs__icon-play');
      const pauseIcon = toggle.querySelector('.kvs__icon-pause');

      let inlineMedia = null;
      let inlinePlaying = false;

      const setIcon = (playing) => {
        if (playIcon) playIcon.hidden = playing;
        if (pauseIcon) pauseIcon.hidden = !playing;
      };

      const startInline = () => {
        if (inlineMedia) return;
        const mediaEl = buildMedia(card, { muted: true, autoplay: true, loop: true });
        if (!mediaEl) return;

        const poster = mediaWrap.querySelector('.kvs__poster');
        mediaWrap.insertBefore(mediaEl, mediaWrap.firstChild);
        if (poster) poster.style.display = 'none';

        inlineMedia = mediaEl;
        if (mediaEl.tagName === 'VIDEO') {
          mediaEl.play().catch(() => {});
        }
        inlinePlaying = true;
        setIcon(true);
      };

      const pauseInline = () => {
        if (!inlineMedia) return;
        if (inlineMedia.tagName === 'VIDEO') {
          inlineMedia.pause();
        } else {
          const type = inlineMedia.dataset.kvsEmbedType;
          const id = inlineMedia.dataset.kvsEmbedId;
          inlineMedia.src = buildEmbedSrc(type, id, { muted: true, autoplay: false, loop: true });
        }
        inlinePlaying = false;
        setIcon(false);
      };

      const resumeInline = () => {
        if (!inlineMedia) return;
        if (inlineMedia.tagName === 'VIDEO') {
          inlineMedia.play().catch(() => {});
        } else {
          const type = inlineMedia.dataset.kvsEmbedType;
          const id = inlineMedia.dataset.kvsEmbedId;
          inlineMedia.src = buildEmbedSrc(type, id, { muted: true, autoplay: true, loop: true });
        }
        inlinePlaying = true;
        setIcon(true);
      };

      if (idx === 0) {
        startInline();
      }

      toggle.addEventListener('click', (event) => {
        event.stopPropagation();
        if (!inlineMedia) {
          startInline();
        } else if (inlinePlaying) {
          pauseInline();
        } else {
          resumeInline();
        }
      });

      mediaWrap.addEventListener('click', () => {
        openFullscreen(idx);
      });
    });
  };

  document.querySelectorAll('.kvs').forEach(initKvs);

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('.kvs').forEach(initKvs);
  });
})();
